import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { createThread, sendMessageToThread } from '../services/openai';

/**
 * Custom hook for managing OpenAI Assistant thread lifecycle
 * 
 * Handles:
 * - Thread initialization on component mount
 * - Linking threads to conversations in Firestore
 * - Sending messages to the thread
 * - Thread state management
 * 
 * @param conversationId - Current conversation ID
 * @param onError - Error callback function
 */
export const useThreadManagement = (
    conversationId: string,
    onError?: (error: string) => void
) => {
    const [threadId, setThreadId] = useState<string | null>(null);
    const [isInitializingThread, setIsInitializingThread] = useState(true);
    const [initError, setInitError] = useState<string | null>(null);

    /**
     * Initialize or retrieve thread for the conversation
     */
    useEffect(() => {
        const initializeThread = async () => {
            if (!conversationId) {
                setIsInitializingThread(false);
                return;
            }

            setIsInitializingThread(true);
            setInitError(null);

            try {
                // Check if conversation already has a threadId
                const conversationRef = doc(db, `conversations/${conversationId}`);
                const conversationDoc = await getDoc(conversationRef);

                if (conversationDoc.exists()) {
                    const conversationData = conversationDoc.data();

                    if (conversationData.threadId) {
                        // Use existing thread
                        console.log('useThreadManagement: Using existing thread:', conversationData.threadId);
                        setThreadId(conversationData.threadId);
                    } else {
                        // Create new thread and save it
                        console.log('useThreadManagement: Creating new thread for conversation:', conversationId);
                        const newThreadId = await createThread();
                        console.log('useThreadManagement: Created thread:', newThreadId);

                        // Save thread ID to conversation
                        await updateDoc(conversationRef, {
                            threadId: newThreadId
                        });

                        setThreadId(newThreadId);
                    }
                } else {
                    const error = 'Conversation not found';
                    setInitError(error);
                    onError?.(error);
                }
            } catch (error) {
                console.error('useThreadManagement: Error initializing thread:', error);
                const errorMessage = error instanceof Error ? error.message : 'Error initializing thread';
                setInitError(errorMessage);
                onError?.(errorMessage);
            } finally {
                setIsInitializingThread(false);
            }
        };

        initializeThread();
    }, [conversationId]); // Removed onError to prevent infinite loop


    /**
     * Send a message to the thread
     * @param message - Message content to send
     * @returns AI response text
     */
    const sendToThread = useCallback(async (message: string): Promise<string> => {
        if (!threadId) {
            throw new Error('Thread not initialized');
        }

        try {
            console.log('useThreadManagement: Sending message to thread:', threadId);
            const response = await sendMessageToThread(threadId, message);
            console.log('useThreadManagement: Received response from thread');
            return response;
        } catch (error) {
            console.error('useThreadManagement: Error sending message to thread:', error);
            throw error;
        }
    }, [threadId]);

    /**
     * Check if thread is ready to use
     */
    const isThreadReady = !isInitializingThread && !!threadId && !initError;

    return {
        threadId,
        isInitializingThread,
        isThreadReady,
        initError,
        sendToThread
    };
};
